import { QueryClient } from "@tanstack/react-query";
import { LoaderFunctionArgs, redirect } from "react-router";

import { FileService, IngestionStatus } from "@/shared/domain/files";
import { fileQueryKeys } from "@/shared/domain/files/hooks/fileQueryKeys";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { Goto } from "@/shared/navigation";

/**
 * Loader function for the /files/:fileId route.
 * Fetches the file record and redirects to /visualize if the file is ingested.
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs): Promise<Response | null> => {
    const { fileId, workspaceId } = params;

    // TODO Should this redirect to 404 instead?
    if (!fileId) {
      throw new Error("File ID is required");
    }

    // If any legacy files/workspaceId params are provided, redirect to visualizer
    if (workspaceId) {
      return redirect(`${Goto.route.Visualize}/${workspaceId}`);
    }

    const httpClient = await makeAuthenticatedHttpClient();
    const fileService = new FileService(httpClient);

    // Warm the cache slot `useFile` reads (keyed by the globally-unique fileId)
    // so the File route doesn't refetch on mount. `ensureQueryData` reuses a
    // fresh cached record / in-flight fetch rather than always refetching.
    const fileRecord = await queryClient.ensureQueryData({
      queryKey: fileQueryKeys.detail(fileId),
      queryFn: ({ signal }) =>
        fileService.getFileRecord(fileId, { abortSignal: signal }),
    });

    // If the file is ingested, redirect to /visualize
    if (fileRecord.ingestion_status === IngestionStatus.Ingested) {
      return redirect(`${Goto.route.Visualize}?file=${fileId}`);
    }

    // Otherwise, return null to render the File component
    return null;
  };
